/**
 * Asset URL sanitizer.
 *
 * Why this exists: the backend used to build asset URLs with
 * `str(request.base_url)`, which in our k8s ingress returns the *internal*
 * cluster hostname (e.g. `http://property-decision-4.cluster-5.preview.emergentcf.cloud/...`).
 * That host is unreachable from the browser — every `<img>` rendered as
 * broken until iter-41 r5 switched the backend to path-only URLs.
 *
 * This module is the defensive belt: even if someone re-introduces
 * `request.base_url` later (or another backend leaks an internal host), the
 * frontend will rewrite the host to the public SPA origin so images and
 * videos still load.
 *
 * Two helpers are exported:
 *   - `assetUrl(url)`       — plain function (use in JSX: `<img src={assetUrl(s.image_url)} />`)
 *   - `useAssetUrl(url)`    — React hook that memoises the result.
 */

const PUBLIC_ORIGIN = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

// Patterns that should be REWRITTEN to PUBLIC_ORIGIN if the URL accidentally
// resolves to an internal-only cluster host.
const INTERNAL_HOST_PATTERNS = [
  /\.cluster-\d+\.preview\.emergentcf\.cloud/i,   // most common leak
  /\.preview\.emergentcf\.cloud/i,                // any emergentcf.cloud
  /\.svc\.cluster\.local/i,                       // k8s service DNS
  /^localhost(:\d+)?$/i,                          // 127.0.0.1 leaked from dev
  /^127\.0\.0\.1(:\d+)?$/i,
  /^0\.0\.0\.0(:\d+)?$/i,
];

function isInternalHost(host) {
  if (!host) return false;
  return INTERNAL_HOST_PATTERNS.some((re) => re.test(host));
}

/**
 * Normalise any backend-emitted asset URL to one the browser can actually fetch.
 *
 * Rules:
 *   - `null` / `undefined` / "" → returned untouched (callers usually treat
 *     falsy as "no image yet").
 *   - `data:` and `blob:` URIs → returned untouched.
 *   - Absolute URL with an internal-cluster host → host rewritten to PUBLIC_ORIGIN,
 *     path + query preserved. A console.warn is emitted (once per unique URL)
 *     so this regression is caught in dev.
 *   - Absolute URL with any other host → returned untouched (third-party CDN,
 *     S3, fal.ai, etc.).
 *   - Relative path starting with `/` → prefixed with PUBLIC_ORIGIN.
 *   - Anything else → returned untouched (let the browser try).
 */
const _warned = new Set();
export function assetUrl(url) {
  if (!url || typeof url !== "string") return url;
  const u = url.trim();
  if (!u) return u;
  if (u.startsWith("data:") || u.startsWith("blob:")) return u;
  if (u.startsWith("/")) {
    // Path-only — resolve against public origin.
    return PUBLIC_ORIGIN ? `${PUBLIC_ORIGIN}${u}` : u;
  }
  if (/^https?:\/\//i.test(u)) {
    try {
      const parsed = new URL(u);
      if (isInternalHost(parsed.hostname)) {
        if (!_warned.has(u)) {
          _warned.add(u);
          // eslint-disable-next-line no-console
          console.warn(
            `[assetUrl] Backend leaked an internal host (${parsed.hostname}) ` +
            `for ${parsed.pathname}. Rewriting to ${PUBLIC_ORIGIN}. ` +
            `Long-term fix: backend should return path-only URLs.`
          );
        }
        const rewritten = `${PUBLIC_ORIGIN}${parsed.pathname}${parsed.search}${parsed.hash}`;
        return rewritten;
      }
      return u;
    } catch {
      return u;
    }
  }
  // protocol-relative `//host/path` or anything else — leave alone.
  return u;
}

// Hook variant — keeps memoisation around so the same input doesn't churn renders.
import { useMemo } from "react";
export function useAssetUrl(url) {
  return useMemo(() => assetUrl(url), [url]);
}

export default assetUrl;
